/**
 * 网络状态检测工具
 * 使用 fetch API 检测网络连接状态
 */
import { request } from "@saas/service-api/src/request.js";
class NetworkDetector {
  constructor(options = {}) {
    this.options = {
      timeout: 5000, // 超时时间 5秒
      checkInterval: 3000, // 检查间隔 3秒
      retryCount: 2, // 重试次数
      checkUrl: "/favicon.ico", // 检测URL
      ...options,
    };

    this.isOnline = navigator.onLine;
    this.listeners = new Map();
    this.checkTimer = null;
    this.isChecking = false;
  }

  // 添加事件监听
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // 移除事件监听
  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // 触发事件
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        callback(data);
      });
    }
  }

  // 检测网络状态
  async checkNetworkStatus() {
    if (this.isChecking) return;

    this.isChecking = true;
    const previousStatus = this.isOnline;

    try {
      // 使用 fetch 检测网络
      const isOnline = await this.checkByFetch();
      this.isOnline = isOnline;

      // 状态变化时触发事件
      if (previousStatus !== isOnline) {
        this.emit("statusChange", {
          isOnline,
          previousStatus,
          timestamp: Date.now(),
        });
      }

      this.emit("checkComplete", {
        isOnline,
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error("网络检测失败:", error);
      this.emit("error", error);
    } finally {
      this.isChecking = false;
    }
  }

  // 使用 fetch 检测网络
  async checkByFetch() {
    let lastError = null;

    // 重试机制
    for (let i = 0; i <= this.options.retryCount; i++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          this.options.timeout
        );

        const response = await request(
          this.options.checkUrl,
          "get",
          {},
          this.options.timeout
        );
        clearTimeout(timeoutId);

        // 只要有响应就说明网络是通的
        if (response !== null && response !== undefined) {
          console.log("网络检测成功:", response);
          return true;
        } else {
          console.log("网络检测响应为空:", response);
          return false;
        }
      } catch (error) {
        lastError = error;
        console.log(
          `网络检测失败 (尝试 ${i + 1}/${this.options.retryCount + 1}):`,
          error.message
        );

        // 如果不是最后一次尝试，等待一段时间再重试
        if (i < this.options.retryCount) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }

    // 所有重试都失败了
    console.log("网络检测最终失败:", lastError?.message);
    return false;
  }

  // 开始监控
  startMonitoring() {
    if (this.checkTimer) return;
    console.log("startMonitoring");

    // 立即检测一次
    this.checkNetworkStatus();

    // 设置定时检测
    this.checkTimer = setInterval(() => {
      this.checkNetworkStatus();
    }, this.options.checkInterval);

    // 监听浏览器在线状态
    window.addEventListener("online", () => {
      this.isOnline = true;
      this.emit("statusChange", {
        isOnline: true,
        previousStatus: false,
        timestamp: Date.now(),
      });
    });

    window.addEventListener("offline", () => {
      this.isOnline = false;
      this.emit("statusChange", {
        isOnline: false,
        previousStatus: true,
        timestamp: Date.now(),
      });
    });
  }

  // 停止监控
  stopMonitoring() {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
  }

  // 获取当前状态
  getStatus() {
    return {
      isOnline: this.isOnline,
      timestamp: Date.now(),
    };
  }
}

// 创建默认实例
const networkDetector = new NetworkDetector();

// 导出
export default networkDetector;
export { NetworkDetector };
